import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Preferences } from '@capacitor/preferences';
import { FirebaseAuthentication, type User } from '@capacitor-firebase/authentication';
import { api } from '@/utils/api';
import { useIonToast } from '@ionic/react';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { signOut } from '@/utils/capacitorUtils';

// Debug Mode Context
interface DebugModeContextType {
  isDebugMode: boolean;
  toggleDebugMode: () => Promise<void>;
}

export const DebugModeContext = createContext<DebugModeContextType>({
  isDebugMode: false,
  toggleDebugMode: async () => { }
});

export function useDebugMode() {
  return useContext(DebugModeContext);
}

export function DebugModeProvider({ children }: { children: React.ReactNode }) {
  const [isDebugMode, setIsDebugMode] = useState(false);

  // Load initial debug state
  useEffect(() => {
    const loadDebugState = async () => {
      const { value } = await Preferences.get({ key: 'isDebug' });
      setIsDebugMode(value === 'true');
    };

    loadDebugState();

    // Set up listener for preference changes
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Handle storage changes from other components
  const handleStorageChange = async (event: StorageEvent) => {
    if (event.key === 'isDebug' || event.key === null) {
      const { value } = await Preferences.get({ key: 'isDebug' });
      setIsDebugMode(value === 'true');
    }
  };

  // Toggle debug mode
  const toggleDebugMode = async () => {
    const newDebugMode = !isDebugMode;
    setIsDebugMode(newDebugMode);

    await Preferences.set({
      key: 'isDebug',
      value: newDebugMode.toString()
    });

    // Dispatch storage event to notify other components
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'isDebug',
      newValue: newDebugMode.toString()
    }));

    console.log(`Debug mode ${newDebugMode ? 'enabled' : 'disabled'}`);
  };

  return (
    <DebugModeContext.Provider value={{ isDebugMode, toggleDebugMode }}>
      {children}
    </DebugModeContext.Provider>
  );
}

// Define types for the user profile and membership
interface UserProfile {
  id: number;
  userId: string;
  imageUrl: string;
  name: string;
  countryCode: string;
  username: string;
  email: string;
  membershipId?: string;
}

interface Membership {
  id: string;
  name: string;
  startDate: string;
}

interface UserProfileContextType {
  profile: UserProfile | null;
  membership: Membership | null;
  refreshProfile: () => Promise<void>;
  isProfileLoading: boolean;
}

// Create the UserProfile context
const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

// Custom hook to use the UserProfile context
function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }
  return context;
}

// UserProfileProvider Component
function UserProfileProvider({ children }: { children: React.ReactNode }) {
  const CACHE_TIMEOUT = 1000 * 60 * 60 * 24; // Cache timeout (24 hours)
  const RETRY_DELAY = 2000; // Retry delay in ms
  const MAX_RETRIES = 3; // Maximum number of retries

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const retryCount = useRef(0);
  const [presentToast] = useIonToast();
  const initialLoadComplete = useRef(false);

  // Helper function to check if the cache is still valid
  const isCacheValid = (timestamp?: number) => timestamp && Date.now() - timestamp < CACHE_TIMEOUT;

  // Function to clear profile data on sign out
  const clearProfileData = async () => {
    console.log('Clearing profile data');
    setProfile(null);
    setMembership(null);
    setUser(null);

    try {
      // Clear cached profile data
      await Preferences.remove({ key: 'userProfile' });
      await Preferences.remove({ key: 'userMembership' });
      await Preferences.remove({ key: 'userProfileTimestamp' });
      console.log('Profile data cleared successfully');
    } catch (err) {
      console.error('Error clearing profile data:', err);
      Sentry.captureException(err);
    }
  };

  // Load cached profile on startup and set up listeners
  useEffect(() => {
    const loadCachedProfile = async () => {
      try {
        const cachedProfile = await Preferences.get({ key: 'userProfile' });
        const cachedMembership = await Preferences.get({ key: 'userMembership' });

        if (cachedProfile.value) {
          const profileData = JSON.parse(cachedProfile.value);
          setProfile(profileData);
          console.log('Loaded cached profile on startup:', profileData.username);

          if (cachedMembership.value) {
            setMembership(JSON.parse(cachedMembership.value));
          }
        }
      } catch (err) {
        // Capture the error in Sentry with additional context
        Sentry.captureException(err, {
          extra: {
            context: 'Loading cached profile',
            initialLoadComplete: initialLoadComplete.current
          }
        });
        console.error('Error loading cached profile:', err);
      }
    };

    // Set up direct sign-out event listener
    const handleUserSignout = async () => {
      console.log('User signout event received');
      await clearProfileData();
    };

    // Set up auth state change listener to refresh profile on sign in
    const setupAuthListener = async () => {
      await FirebaseAuthentication.addListener('authStateChange', async (event) => {
        // If user signed in, refresh the profile
        if (event.user) {
          console.log('User signed in, refreshing profile');
          fetchUserProfile(true); // Force refresh to get latest data
        }
      });
    };

    // Add event listener for custom sign-out event
    window.addEventListener('user-signout', handleUserSignout);

    loadCachedProfile();
    setupAuthListener();

    // Clean up listeners on unmount
    return () => {
      window.removeEventListener('user-signout', handleUserSignout);
      FirebaseAuthentication.removeAllListeners();
    };
  }, []);

  // Function to fetch the user profile (with cache handling)
  const fetchUserProfile = async (forceRefresh = false) => {
    setIsProfileLoading(true);
    setErrorMessage(null);

    try {
      // Check if user is authenticated
      const currentUser = await FirebaseAuthentication.getCurrentUser();

      if (!currentUser.user) {
        // Capture the error in Sentry with additional context
        Sentry.captureException(new Error('No authenticated user found'), {
          extra: {
            context: 'Fetching user profile',
            initialLoadComplete: initialLoadComplete.current
          }
        });
        console.log('No authenticated user found');
        setIsProfileLoading(false);
        return;
      }

      // Always try to use cached data first, unless force refresh
      if (!forceRefresh) {
        const cachedProfile = await Preferences.get({ key: 'userProfile' });
        const cachedMembership = await Preferences.get({ key: 'userMembership' });
        const cachedTimestamp = await Preferences.get({ key: 'userProfileTimestamp' });

        const profileData = cachedProfile.value ? JSON.parse(cachedProfile.value) : null;
        const membershipData = cachedMembership.value ? JSON.parse(cachedMembership.value) : null;
        const timestamp = cachedTimestamp.value ? parseInt(cachedTimestamp.value, 10) : 0;

        // Use cached data if valid
        if (profileData && isCacheValid(timestamp)) {
          console.log('Using cached profile data for', profileData.username);
          setProfile(profileData);
          setMembership(membershipData);
          setIsProfileLoading(false);
          initialLoadComplete.current = true;
          return;
        }
      }

      // Load cached profile before attempting network request
      const cachedProfile = await Preferences.get({ key: 'userProfile' });
      const cachedMembership = await Preferences.get({ key: 'userMembership' });
      const cachedProfileData = cachedProfile.value ? JSON.parse(cachedProfile.value) : null;
      const cachedMembershipData = cachedMembership.value ? JSON.parse(cachedMembership.value) : null;

      // Fetch fresh data from the API
      try {
        // Check if we're online before making the request
        if (!navigator.onLine) {
          console.log('Device is offline, using cached data if available');
          throw new AxiosError('Device is offline', 'ERR_NETWORK');
        }

        // Add network status to Sentry breadcrumb
        Sentry.addBreadcrumb({
          category: 'network',
          message: `Network status: ${navigator.onLine ? 'online' : 'offline'}`,
          level: 'info'
        });

        console.log('Fetching fresh profile data...');

        // Set up a timeout for the API call
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
          console.log('API call timed out after 8 seconds');
        }, 8000); // 8 second timeout for mobile-first experience

        const { data: newProfileData } = await api.get('/profile', {
          signal: controller.signal,
          timeout: 8000 // Also set axios timeout to 8 seconds
        });

        // Add defensive check for the expected data format
        if (!newProfileData) {
          throw new Error('Profile data is empty or undefined');
        }

        // Log the data type for debugging
        console.log('Profile data type:', typeof newProfileData, Array.isArray(newProfileData));
        Sentry.addBreadcrumb({
          category: 'profile',
          message: `Profile data type: ${typeof newProfileData}, isArray: ${Array.isArray(newProfileData)}`,
          level: 'info'
        });

        let profileToUse = newProfileData;

        // Handle case where data might be an array instead of an object (the issue in your screenshot)
        if (Array.isArray(newProfileData)) {
          console.warn('Profile data is an array instead of an object');
          Sentry.captureMessage('Profile data is an array', {
            level: 'warning',
            extra: {
              profileData: typeof newProfileData === 'object' ? JSON.stringify(newProfileData) : String(newProfileData),
              dataLength: Array.isArray(newProfileData) ? newProfileData.length : 'not array'
            }
          });

          // If it's an array with data, use the first item
          if (newProfileData.length > 0) {
            console.log('Using first item from profile data array');
            profileToUse = newProfileData[0];
          } else {
            throw new Error('Profile data is an empty array');
          }
        }

        // Final check for required properties
        if (typeof profileToUse !== 'object' || !profileToUse) {
          throw new Error(`Invalid profile data format: ${typeof profileToUse}`);
        }

        // Check if the profile has the expected properties
        if (!profileToUse.username) {
          console.warn('Profile data missing username:', profileToUse);
          Sentry.captureMessage('Profile data missing username', {
            level: 'warning',
            extra: { profileData: JSON.stringify(profileToUse) }
          });
        }

        console.log('Using profile data:', profileToUse.username || 'unknown username');
        setProfile(profileToUse);

        // Cache the new profile data and update the timestamp
        await Preferences.set({ key: 'userProfile', value: JSON.stringify(newProfileData) });
        await Preferences.set({ key: 'userProfileTimestamp', value: Date.now().toString() });

        // If the profile includes a membership ID, fetch the membership data
        if (newProfileData?.membershipId) {
          const { data: newMembershipData } = await api.get(`/golf-clubs/${newProfileData.membershipId}`);
          setMembership(newMembershipData);

          // Cache the membership data
          await Preferences.set({ key: 'userMembership', value: JSON.stringify(newMembershipData) });
        } else {
          // If no membership ID exists, clear cached membership data
          setMembership(null);
          await Preferences.remove({ key: 'userMembership' });
        }

        // Clean up timeout
        clearTimeout(timeoutId);

        // Reset retry counter on success
        retryCount.current = 0;
      }
      catch (error) {
        // Capture the error in Sentry with additional context
        Sentry.captureException(error, {
          extra: {
            context: 'Fetching user profile',
            retryCount: retryCount.current,
            initialLoadComplete: initialLoadComplete.current
          }
        });

        if (error instanceof AxiosError && error.response?.status === 404) {
          console.log('No profile associated with this user');
          // Keep any cached profile instead of setting to null
          const cachedProfile = await Preferences.get({ key: 'userProfile' });
          if (!cachedProfile.value) {
            setProfile(null);
          }
        } else if (error instanceof AxiosError) {
          console.error('Error fetching profile:', error.message, error.response?.data);

          // Enhanced network error handling
          if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || !navigator.onLine) {
            console.log('Network error detected, using cached profile if available');

            // Increment retry count but cap it
            retryCount.current = Math.min(retryCount.current + 1, 5);

            // Use cached profile if available
            if (cachedProfileData) {
              Sentry.captureMessage('Using cached profile due to network error', {
                level: 'info',
                extra: {
                  profileData: JSON.stringify(cachedProfileData),
                  errorCode: error.code,
                  errorMessage: error.message,
                  retryCount: retryCount.current
                }
              });
              console.log('Using cached profile due to network error:', cachedProfileData.username || 'unknown');
              setProfile(cachedProfileData);
              setMembership(cachedMembershipData);

              // Show toast notification about using cached data
              presentToast({
                message: 'Using cached profile data (offline mode)',
                duration: 3000,
                position: 'bottom',
                color: 'warning'
              });
              return;
            }
          }

          // On other errors, retry a few times before giving up
          if (retryCount.current < MAX_RETRIES) {
            retryCount.current += 1;
            console.log(`Retrying profile fetch (${retryCount.current}/${MAX_RETRIES}) in ${RETRY_DELAY}ms`);

            setTimeout(() => {
              fetchUserProfile(forceRefresh);
            }, RETRY_DELAY);
            return;
          }

          // If we've exhausted retries, use cached profile if available
          if (cachedProfileData) {
            console.log('Using cached profile after retry failure:', cachedProfileData.username || 'unknown');
            setProfile(cachedProfileData);
            setMembership(cachedMembershipData);

            presentToast({
              message: 'Using cached profile data (server unavailable)',
              duration: 3000,
              position: 'bottom',
              color: 'warning'
            });
          } else {
            setErrorMessage('Failed to fetch user profile after multiple attempts.');
            signOut();
          }
        } else {
          // Handle non-Axios errors (like the map is not a function error)
          console.error('Unknown error occurred', error);

          // Show a more specific error message if possible
          let errorMessage = 'An unexpected error occurred. Please try again.';
          if (error instanceof Error) {
            errorMessage = `Error: ${error.message}`;
          }

          presentToast({
            message: errorMessage,
            duration: 3000,
            position: 'bottom',
            color: 'danger'
          });

          Sentry.captureException(error, {
            extra: {
              context: 'Fetching user profile',
              initialLoadComplete: initialLoadComplete.current
            }
          });

          // Use cached profile if available for any error
          if (cachedProfileData) {
            console.log('Using cached profile after unknown error:', cachedProfileData.username || 'unknown');
            setProfile(cachedProfileData);
            setMembership(cachedMembershipData);
          }
        }
      }
    } finally {
      setIsProfileLoading(false);
      initialLoadComplete.current = true;
    }
  };

  // Function to manually refresh the profile
  const refreshProfile = async () => {
    await fetchUserProfile(true);
  };

  // Initialize auth state and profile on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get current user state
        const result = await FirebaseAuthentication.getCurrentUser();
        setUser(result.user);

        if (result.user) {
          // If user is authenticated, fetch profile
          fetchUserProfile();
        } else {
          // If no user, clear profile state
          setProfile(null);
          setMembership(null);
          setIsProfileLoading(false);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setIsProfileLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Listen to changes in the user's authentication state
  useEffect(() => {
    const setupAuthListener = async () => {
      FirebaseAuthentication.addListener('authStateChange', async (result) => {
        setUser(result.user);

        if (!result.user) {
          // Clear profile and membership when the user logs out
          setProfile(null);
          setMembership(null);
          setIsProfileLoading(false);

          // Clear cached data on logout
          await Preferences.remove({ key: 'userProfile' });
          await Preferences.remove({ key: 'userMembership' });
          await Preferences.remove({ key: 'userProfileTimestamp' });
        } else {
          // User logged in, fetch profile
          fetchUserProfile();
        }
      });
    };

    setupAuthListener();

    return () => {
      FirebaseAuthentication.removeAllListeners();
    };
  }, []);

  // Periodically refresh profile data when app is active
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(() => {
      console.log('Periodic profile refresh');
      fetchUserProfile();
    }, CACHE_TIMEOUT / 2); // Refresh at half the cache timeout

    return () => clearInterval(refreshInterval);
  }, [user]);

  return (
    <UserProfileContext.Provider value={{ profile, membership, refreshProfile, isProfileLoading }}>
      {children}
    </UserProfileContext.Provider>
  );
}

export { UserProfileProvider, useUserProfile };